<template>
  <div>
    <b-card-body
      class="border-bottom d-flex"
      style="gap: 1rem"
    >
      <filter-list-component v-on:filter="filterList"></filter-list-component>

      <b-input-group :prepend="this.$t('altbestaende.minimumStoragePeriod')">
        <b-form-select v-model="selectedMinimumStoragePeriod">
          <b-form-select-option :value="0">
            {{ $t("altbestaende.filterAll") }}
          </b-form-select-option>
          <b-form-select-option :value="90">
            {{ $t("altbestaende.filterNinetyPlusDays") }}
          </b-form-select-option>
          <b-form-select-option :value="180">
            {{ $t("altbestaende.filterOnehundredEightyPlusDays") }}
          </b-form-select-option>
          <b-form-select-option :value="365">
            {{ $t("altbestaende.filterOnePlusYears") }}
          </b-form-select-option>
          <b-form-select-option :value="730">
            {{ $t("altbestaende.filterTwoPlusYears") }}
          </b-form-select-option>
        </b-form-select>
      </b-input-group>
      <b-button
        variant="outline-primary"
        class="ml-auto"
        style="white-space: nowrap"
        @click="exportXLSX"
      >
        <font-awesome-icon
          icon="file-export"
          class="mr-2"
        />
        {{ $t("altbestaende.exportXLSX") }}
      </b-button>
      <b-button
        variant="outline-primary"
        class="ml-auto"
        style="white-space: nowrap"
        @click="exportDeluxeArticleList"
      >
        <font-awesome-icon
          icon="file-export"
          class="mr-2"
        />
        {{ $t("altbestaende.exportDeluxeArticleList") }}
      </b-button>
    </b-card-body>
    <b-table
      striped
      :items="paginatedItems"
      :fields="fields"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :per-page="0"
      :current-page="1"
      show-empty
      :empty-text="$t('global.keineDatenVorhanden')"
      no-local-sorting
      ref="selectableTable"
    >
      <template #cell(storagePeriod)="data">
        <span
          :class="{
            'text-danger': data.item.storagePeriod > 365,
            'text-warning':
              data.item.storagePeriod >= 180 && data.item.storagePeriod <= 365,
          }"
        >
          {{ formatNumber(data.item.storagePeriod) }} {{ $t("global.tage") }}
        </span>
      </template>
      <template #cell(quantity)="data">
        <span>{{ formatNumber(data.item.quantity) }}</span>
      </template>
      <template #cell(reservedQuantity)="data">
        <span>{{ formatNumber(data.item.reservedQuantity) }}</span>
      </template>
      <template #cell(purchasePrice)="data">
        <span>{{ formatCurrency(data.item.purchasePrice) }}</span>
      </template>
      <template #cell(value)="data">
        <span>{{ formatCurrency(data.item.value) }}</span>
      </template>
    </b-table>

    <!-- Pagination -->
    <div
      class="d-flex justify-content-between align-items-center mt-3 px-3 pb-3"
    >
      <div class="text-muted">
        {{ $t("global.zeige") }} {{ formatNumber(startItem) }} -
        {{ formatNumber(endItem) }} {{ $t("global.von") }}
        {{ formatNumber(totalFilteredItems) }} {{ $t("global.eintraegen") }}
      </div>
      <b-pagination
        v-model="currentPage"
        :total-rows="totalFilteredItems"
        :per-page="perPage"
        :aria-controls="'altbestaende-table'"
        size="sm"
        class="mb-0"
        first-number
        last-number
        limit="5"
      ></b-pagination>
      <div>
        <b-form-select
          v-model="perPage"
          :options="perPageOptions"
          size="sm"
          style="width: auto"
        ></b-form-select>
      </div>
    </div>
  </div>
</template>

<script>
  import FilterListComponent from "../FilterListComponent.vue";
  import * as XLSX from "xlsx";

  export default {
    name: "AltbestaendeTabelle",
    components: {
      FilterListComponent,
    },
    props: {
      businessUnit: Object,
    },

    data() {
      return {
        // Tabellenkonfiguration
        fields: [
          {
            key: "articleNumber",
            label: this.$t("global.artikelnummer"),
            sortable: true,
          },
          {
            key: "manufacturer",
            label: this.$t("global.hersteller"),
            sortable: true,
          },
          {key: "name", label: this.$t("global.name"), sortable: true},
          {
            key: "arrivalDate",
            label: this.$t("global.anlieferungsdatum"),
            sortable: true,
          },
          {
            key: "storagePeriod",
            label: this.$t("global.lagerdauer"),
            sortable: true,
          },
          {
            key: "storageLocation",
            label: this.$t("global.lagerort"),
            sortable: true,
          },
          {key: "quantity", label: this.$t("global.menge"), sortable: true},
          {
            key: "reservedQuantity",
            label: this.$t("global.reservierteMenge"),
            sortable: true,
          },
          {
            key: "purchasePrice",
            label: this.$t("global.einkaufspreis"),
            sortable: true,
          },
          {key: "value", label: this.$t("global.wert"), sortable: true},
        ],

        // Filter-Konfiguration
        selectedMinimumStoragePeriod: 90,
        filter: "",

        // Sortierung
        sortBy: "storagePeriod",
        sortDesc: false,

        // Paginierung
        currentPage: 1,
        perPage: 25,
        perPageOptions: [
          {value: 10, text: "10"},
          {value: 25, text: "25"},
          {value: 50, text: "50"},
          {value: 100, text: "100"},
        ],
      };
    },
    computed: {
      filteredItems() {
        console.log(
          "AltbestaendeTabelle - Store Altbestände:",
          this.$store.state.altbestaende.length
        );

        return this.$store.state.altbestaende.filter(this.applyFilters);
      },
      sortedItems() {
        const items = [...this.filteredItems];

        if (this.sortBy) {
          items.sort((a, b) => {
            const result = this.customCompare(a, b, this.sortBy);
            return this.sortDesc ? -result : result;
          });
        }

        return items;
      },

      totalFilteredItems() {
        return this.sortedItems.length;
      },

      paginatedItems() {
        return this.perPage === 0
          ? this.sortedItems
          : this.getCurrentPageItems();
      },

      startItem() {
        if (this.totalFilteredItems === 0) return 0;
        return this.perPage === 0
          ? 1
          : (this.currentPage - 1) * this.perPage + 1;
      },

      endItem() {
        if (this.totalFilteredItems === 0) return 0;
        if (this.perPage === 0) return this.totalFilteredItems;
        const end = this.currentPage * this.perPage;
        return Math.min(end, this.totalFilteredItems);
      },
    },
    methods: {
      // === Filter-Methoden ===
      applyFilters(item) {
        const storagePeriod = item.storagePeriod || 0;
        const meetsStorageFilter =
          storagePeriod >= this.selectedMinimumStoragePeriod;

        if (this.filter && this.filter.trim() !== "") {
          const searchTerm = this.filter.toLowerCase();
          const matchesSearch =
            (item.articleNumber || "").toLowerCase().includes(searchTerm) ||
            (item.manufacturer || "").toLowerCase().includes(searchTerm) ||
            (item.name || "").toLowerCase().includes(searchTerm);
          return meetsStorageFilter && matchesSearch;
        }

        return meetsStorageFilter;
      },

      filterList(filter) {
        this.filter = filter;
      },

      // === Paginierung ===
      getCurrentPageItems() {
        const start = (this.currentPage - 1) * this.perPage;
        const end = start + this.perPage;
        return this.sortedItems.slice(start, end);
      },

      resetToFirstPage() {
        this.currentPage = 1;
      },

      // === Export-Methoden ===
      exportXLSX() {
        // Erstelle Arbeitsblatt-Daten
        const worksheetData = [
          this.getCSVHeaders(),
          ...this.sortedItems.map(this.mapItemToXLSXRow),
        ];

        // Erstelle neues Arbeitsblatt
        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

        // Setze Spaltenbreiten
        const columnWidths = [
          {wch: 15}, // Artikelnummer
          {wch: 20}, // Hersteller
          {wch: 30}, // Name
          {wch: 15}, // Anlieferungsdatum
          {wch: 15}, // Lagerdauer
          {wch: 15}, // Lagerort
          {wch: 10}, // Menge
          {wch: 18}, // Reservierte Menge
          {wch: 15}, // Einkaufspreis
          {wch: 15}, // Wert
        ];
        worksheet["!cols"] = columnWidths;

        // Erstelle neue Arbeitsmappe
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Altbestände");

        // Generiere Dateiname mit aktuellem Datum
        const now = new Date();
        const dateStr = now.toISOString().split("T")[0];
        const filename = `altbestaende_${dateStr}.xlsx`;

        // Schreibe und lade Datei herunter
        XLSX.writeFile(workbook, filename);

        console.log(
          `XLSX Export: ${this.sortedItems.length} Artikel exportiert`
        );
      },

      exportDeluxeArticleList() {
        const articleNumbers = this.sortedItems.map(
          (item) => item.articleNumber || ""
        );
        const textContent = articleNumbers.join("\n");

        this.downloadFile(
          textContent + "\n",
          "text/plain;charset=utf-8;",
          "deluxe_artikelnummern",
          "txt"
        );
        console.log(
          `Deluxe-Artikelmenge Export: ${articleNumbers.length} Artikelnummern exportiert`
        );
      },

      // === Hilfsmethoden für Export ===
      mapItemToXLSXRow(item) {
        return [
          item.articleNumber || "",
          item.manufacturer || "",
          item.name || "",
          item.arrivalDate || "",
          item.storagePeriod || 0,
          item.storageLocation || "",
          item.quantity || 0,
          item.reservedQuantity || 0,
          parseFloat(item.purchasePrice || 0), // Numerischer Wert für Excel
          parseFloat(item.value || 0), // Numerischer Wert für Excel
        ];
      },

      getCSVHeaders() {
        return [
          this.$t("global.artikelnummer"),
          this.$t("global.hersteller"),
          this.$t("global.name"),
          this.$t("global.anlieferungsdatum"),
          this.$t("altbestaende.headerLagerdauerTage"),
          this.$t("global.lagerort"),
          this.$t("global.menge"),
          this.$t("global.reservierteMenge"),
          this.$t("global.einkaufspreis"),
          this.$t("global.wert"),
        ];
      },

      downloadFile(content, mimeType, filename, extension) {
        const blob = new Blob([content], {type: mimeType});
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);

        const now = new Date();
        const dateStr = now.toISOString().split("T")[0];
        link.setAttribute("download", `${filename}_${dateStr}.${extension}`);

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      },

      // === Formatierung ===
      formatNumberForExport(value) {
        return new Intl.NumberFormat("de-DE", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value || 0);
      },

      formatCurrency(value) {
        return new Intl.NumberFormat("de-DE", {
          style: "currency",
          currency: "EUR",
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        }).format(value || 0);
      },

      formatNumber(value) {
        return new Intl.NumberFormat("de-DE").format(value || 0);
      },

      // === Sortierung ===
      customCompare(aRow, bRow, key) {
        const numericFields = [
          "storagePeriod",
          "quantity",
          "reservedQuantity",
          "purchasePrice",
          "value",
        ];

        if (numericFields.includes(key)) {
          const a = parseFloat(aRow[key]) || 0;
          const b = parseFloat(bRow[key]) || 0;
          return a < b ? -1 : a > b ? 1 : 0;
        }

        const a = String(aRow[key] || "").toLowerCase();
        const b = String(bRow[key] || "").toLowerCase();
        return a < b ? -1 : a > b ? 1 : 0;
      },
    },
    watch: {
      selectedMinimumStoragePeriod() {
        this.resetToFirstPage();
      },

      filter() {
        this.resetToFirstPage();
      },

      perPage() {
        this.resetToFirstPage();
      },

      sortBy() {
        this.resetToFirstPage();
      },

      sortDesc() {
        this.resetToFirstPage();
      },

      filteredItems(items) {
        // Aktualisiere die Statistiken, wenn sich die gefilterten Artikel ändern
        const stats = {
          gesamtartikel: items.length,
          kritisch: items.filter((item) => item.storagePeriod > 365)
            .length,
          warnung: items.filter(
            (item) => item.storagePeriod >= 180 && item.storagePeriod <= 365
          ).length,
          gesamtwert: items.reduce(
            (sum, item) => sum + (item.value || 0),
            0
          ),
        };
        this.$store.commit("altbestaendeStats", stats);
      },
    },
  };
</script>

<style>
  th {
    white-space: nowrap !important;
  }
  .card-costom-style .card-body {
    padding: 0 !important;
  }
</style>
